import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getFleetsByCurrentSemanticMap } from "Slices/fleets";
import { fetchDeployMissionPlan } from "Slices/missionPlans";
import {
  getIsPickedFleetHasVehicles,
  getMissionOperatorPickedFleetId,
  getOperatorMissionStatus,
} from "Slices/operatorData";
import {
  getCurrentMissionAsset,
  getIsMissionAssetsEmpty,
} from "Slices/operatorMissionAssets";
import {
  getIsMissionAssetsLoaded,
  getIsMissionAssetsLoadFailed,
} from "Slices/operatorMissionAssets/selectors";
import { getOperatorPickedStation } from "Slices/operatorStation";

import { FMSLogger } from "FMSLogger";
import { MISSION_OPERATOR_ACTIONS } from "Utils/constants";
import { storageKeys } from "Utils/localStorageUtils";

import { ReactComponent as HornIcon } from "../../assets/horn-icon.svg";
import { ReactComponent as MapIcon } from "../../assets/map-icon.svg";

import {
  InteractionPaneWrapper,
  CallVehicleButtonStyled,
  EmptyErrorStyled,
  MapViewStyled,
} from "./style";

const missionOperatorLogger = FMSLogger.byPrefix(MISSION_OPERATOR_ACTIONS);

type StationActionsProps = {
  initFleetId: string;
  isPickerDisabled?: boolean;
  shouldUseSavedFleet: boolean;
  handleSelectedFleet: (id: string | null) => void;
};

export const StationActions: FC<StationActionsProps> = memo(
  ({
    initFleetId,
    isPickerDisabled,
    shouldUseSavedFleet,
    handleSelectedFleet,
  }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [wasDeploymentTriggerred, setWasDeploymentTriggerred] =
      useState(false);
    const deployTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pickedStation = useSelector(getOperatorPickedStation);
    const currentMissionAsset = useSelector(getCurrentMissionAsset);
    const isMissionAssetsLoaded = useSelector(getIsMissionAssetsLoaded);
    const isMissionAssetsLoadFailed = useSelector(getIsMissionAssetsLoadFailed);
    const isMissionAssetsEmpty = useSelector(getIsMissionAssetsEmpty);
    const missionOperatorPickedFleetId = useSelector(
      getMissionOperatorPickedFleetId
    );
    const isPickedFleetHasVehicles = useSelector(getIsPickedFleetHasVehicles);
    const fleets = useSelector(getFleetsByCurrentSemanticMap);
    const operatorMissionStatus = useSelector(getOperatorMissionStatus);

    // Reset wasDeploymentTriggerred when operatorMissionStatus changes from NO_MISSION to something else
    // This indicates that the deployment was successful and a mission is now in progress
    useEffect(() => {
      if (operatorMissionStatus !== "NO_MISSION") {
        setWasDeploymentTriggerred(false);
        if (deployTimeoutRef.current) {
          clearTimeout(deployTimeoutRef.current);
          deployTimeoutRef.current = null;
        }
      }
    }, [operatorMissionStatus]);

    // Cleanup timeout on component unmount
    useEffect(() => {
      return () => {
        if (deployTimeoutRef.current) {
          clearTimeout(deployTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (fleets.length === 0) {
        handleSelectedFleet(null);
        return;
      }

      const findFleetById = (fleetId: string | null) =>
        fleets.find((fleet) => fleet.id === fleetId);

      const firstFleet = fleets[0];
      let selectedFleet = firstFleet;

      if (initFleetId) {
        selectedFleet = findFleetById(initFleetId) ?? firstFleet;
      } else if (shouldUseSavedFleet) {
        selectedFleet =
          findFleetById(
            localStorage.getItem(storageKeys.MISSION_OPERATOR_SAVED_FLEET_ID)
          ) ?? firstFleet;
      }
      handleSelectedFleet(selectedFleet.id);
    }, [fleets]);

    const handleDeployMissionAsset = useCallback(() => {
      missionOperatorLogger.debug("Call Vehicle button pressed");
      if (!currentMissionAsset || !missionOperatorPickedFleetId) {
        missionOperatorLogger.error(
          "No currentMissionAsset or missionOperatorPickedFleetId when Call Vehicle button pressed"
        );
        return;
      }

      if (operatorMissionStatus !== "NO_MISSION") {
        missionOperatorLogger.error(
          "Cannot call vehicle while another mission is in progress"
        );
        return;
      }

      // Set wasDeploymentTriggerred to true to disable the button while deployment is in progress
      // This prevents multiple deployment requests while waiting for the current one to complete
      setWasDeploymentTriggerred(true);

      // Set a timeout to reset the button state in case of deployment failure
      // This prevents the button from being permanently disabled
      deployTimeoutRef.current = setTimeout(() => {
        setWasDeploymentTriggerred(false);
        missionOperatorLogger.warn(
          "Deployment timeout - resetting button state"
        );
      }, 10000); // 10 seconds timeout

      dispatch(
        fetchDeployMissionPlan({
          ...currentMissionAsset,
          fleet: missionOperatorPickedFleetId,
          stationId: pickedStation?.stationId,
        })
      );
    }, [
      currentMissionAsset,
      missionOperatorPickedFleetId,
      pickedStation,
      operatorMissionStatus,
    ]);

    // The button is disabled in the following cases:
    // 1. No mission asset or fleet selected
    // 2. Fleet picker is disabled
    // 3. Selected fleet has no vehicles
    // 4. A mission is already in progress (operatorMissionStatus !== "NO_MISSION")
    // 5. A deployment request is currently in progress (wasDeploymentTriggerred)
    const isCallVehicleButtonDisabled =
      !currentMissionAsset ||
      !missionOperatorPickedFleetId ||
      isPickerDisabled ||
      !isPickedFleetHasVehicles ||
      operatorMissionStatus !== "NO_MISSION" ||
      wasDeploymentTriggerred;

    const ErrorMessage = useMemo(() => {
      if (isMissionAssetsEmpty || isMissionAssetsLoadFailed)
        return <EmptyErrorStyled>Error: no mission plans!</EmptyErrorStyled>;

      if (!currentMissionAsset && isMissionAssetsLoaded)
        return (
          <EmptyErrorStyled>
            Error: No appropriate mission plan!
          </EmptyErrorStyled>
        );
      return null;
    }, [
      isMissionAssetsEmpty,
      currentMissionAsset,
      isMissionAssetsLoaded,
      isMissionAssetsLoadFailed,
    ]);

    return (
      <InteractionPaneWrapper>
        <CallVehicleButtonStyled
          disabled={isCallVehicleButtonDisabled}
          onClick={handleDeployMissionAsset}
        >
          Call Vehicle
          <HornIcon />
        </CallVehicleButtonStyled>
        {ErrorMessage}
        <MapViewStyled
          onClick={() => {
            navigate("/");
          }}
        >
          Map view
          <MapIcon />
        </MapViewStyled>
      </InteractionPaneWrapper>
    );
  }
);

export default StationActions;
